import { ReactNode, useState } from 'react';

import {
  bindHover,
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

import { TourKeys, EXTERNAL_LINKS } from '@cast/constants';
import {
  Badge,
  Button,
  Icons,
  List,
  ListItem,
  Tooltip,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import {
  useIsTouchDevice,
  openChat,
  isChatLoaded,
  getDocsLink,
} from '@cast/utils';

import { isDevToolboxEnabled, useToolboxState } from 'core/dev-toolbox';
import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';

import { SidebarCollapseButton } from './SidebarCollapseButton';
import { LayoutDivider } from '../../../LayoutDivider';
import { SidebarPopover } from '../../SidebarPopover';

type Props = {
  title: string;
  icon?: ReactNode;
  subtitle?: string;
  titleBadge?: ReactNode;
  children: ReactNode;
};

const DocsButton = () => {
  const isTouch = useIsTouchDevice();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'docs-menu',
  });
  const popoverState = bindPopover(popupState);

  return (
    <>
      <Button
        size="lg"
        variant="ghost"
        startIcon={<Icons.Book size={20} weight="light" />}
        {...(isTouch ? bindTrigger(popupState) : bindHover(popupState))}
        data-testid="sidebar-docs-btn"
      >
        <Typography variant="P14M" color="textSecondary">
          Docs
        </Typography>
      </Button>
      <SidebarPopover {...popoverState} testId="sidebar-docs-btn">
        <List size="medium" disablePadding sx={{ minWidth: '190px' }}>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            href="/api/v1/auth/academy/sso"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => popoverState.onClose()}
          >
            <Stack direction="row" alignItems="center" gap={4}>
              Academy
              <Box mt={4}>
                <Icons.ArrowSquareOut size="1.3em" color="currentColor" />
              </Box>
            </Stack>
          </ListItem>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            href={getDocsLink('docs')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => popoverState.onClose()}
          >
            <Stack direction="row" alignItems="center" gap={4}>
              Documentation
              <Box mt={4}>
                <Icons.ArrowSquareOut size="1.3em" color="currentColor" />
              </Box>
            </Stack>
          </ListItem>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            target="_blank"
            rel="noreferrer noopener"
            href={EXTERNAL_LINKS.castai_slack_community}
            onClick={() => popoverState.onClose()}
          >
            <Stack direction="row" alignItems="center" gap={4}>
              Community
              <Box mt={4}>
                <Icons.ArrowSquareOut size="1.3em" color="currentColor" />
              </Box>
            </Stack>
          </ListItem>
        </List>
      </SidebarPopover>
    </>
  );
};

const DevToolboxButton = () => {
  const { toggle } = useToolboxState();
  const isAnyToolActive = useToolboxState((it) => it.getIsAnyToolActive());

  return (
    <Tooltip title="Dev toolbox" placement="top">
      <Button
        size="lg"
        variant="ghost"
        onClick={toggle}
        data-testid="sidebar-item-dev-toolbox"
        sx={{ minWidth: 0, px: 8 }}
      >
        <Badge
          displayIf={isAnyToolActive}
          count={0}
          horizontal="right"
          size="dot"
          variant="primary"
          vertical="top"
        >
          <Icons.Wrench size={20} weight="light" />
        </Badge>
      </Button>
    </Tooltip>
  );
};

const HelpButton = () => {
  const [hasClicked, setHasClicked] = useState(false);
  const isPylonLoaded = isChatLoaded();

  return (
    <Tooltip
      title="Check your cookie settings if you don't see the chat bubble."
      placement="top"
      wrapIf={!isPylonLoaded}
      disableHoverListener={!hasClicked}
    >
      <Button
        size="lg"
        variant="ghost"
        startIcon={<Icons.Question size={20} weight="light" />}
        onClick={() => {
          openChat();
          setHasClicked(true);
        }}
        data-testid="sidebar-help-btn"
      >
        <Typography variant="P14M" color="textSecondary">
          Help
        </Typography>
      </Button>
    </Tooltip>
  );
};

export function SidebarLayout(props: Props) {
  const isTourActive = useIsTourActive(TourKeys.OpinionatedPlatform);

  return (
    <Stack height="100%" overflow="hidden">
      <Stack
        divider={<LayoutDivider sx={{ mx: 8, width: 'auto' }} />}
        gap={8}
        overflow="hidden"
        flex={1}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          pl={4}
          pb={8}
          px={8}
        >
          <Stack direction="row" alignItems="center" gap={8}>
            {props.icon}
            <Stack>
              <Stack direction="row" alignItems="center" gap={4}>
                <Typography variant="h6" data-testid="title">
                  {props.title}
                </Typography>
                {props.titleBadge}
              </Stack>
              {props.subtitle && (
                <Typography variant="P14M" color="textSecondary">
                  {props.subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
          {!isTourActive && <SidebarCollapseButton />}
        </Stack>

        <Stack
          divider={<LayoutDivider />}
          gap={8}
          overflow="auto"
          px={8}
          pb={4}
        >
          {props.children}
        </Stack>
      </Stack>

      <Stack>
        <LayoutDivider />
        <Stack
          direction="row"
          gap={4}
          alignItems="center"
          justifyContent="space-between"
          px={8}
          py={4}
        >
          <DocsButton />
          <HelpButton />
          {isDevToolboxEnabled() && <DevToolboxButton />}
        </Stack>
      </Stack>
    </Stack>
  );
}
